import { useState } from "react";
import api from "../api/client";

const DEFAULT_STATUSES = [
  "Applied",
  "Screening",
  "Interview",
  "Technical test",
  "Final interview",
  "Offer received",
  "Accepted",
  "Rejected",
  "Withdrawn",
];

// Shared by the agency candidates page and the direct-employer screens.
// Pushes a status, an optional note, and any number of correspondence
// actions (meeting, document request, assessment, information) to one
// candidate's application.
export default function PushStatusPanel({
  applicationId,
  subtitle,
  employerToken,
  onClose,
  onPushed,
}) {
  const [status, setStatus] = useState("");
  const [customStatus, setCustomStatus] = useState("");
  const [note, setNote] = useState("");
  const [actions, setActions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  const addAction = () => {
    setActions([
      ...actions,
      {
        action_type: "meeting",
        title: "",
        description: "",
        meeting_at: "",
        duration_minutes: "",
        attendees: "",
        url: "",
        deadline: "",
        guidelines: "",
      },
    ]);
  };

  // A meeting time or deadline already in the past is almost always a typo.
  // Checked on push because the min attribute only greys out the picker in
  // some browsers and never blocks a typed value.
  const actionHasPastTime = (a) => {
    const value =
      a.action_type === "meeting"
        ? a.meeting_at
        : a.action_type === "info"
          ? null
          : a.deadline;
    return Boolean(value) && new Date(value) < new Date();
  };

  // A meeting without a time, or a request or assessment without a deadline,
  // would reach the candidate as an empty promise.
  const actionMissingTime = (a) => {
    if (a.action_type === "meeting") return !a.meeting_at;
    if (a.action_type === "document_request" || a.action_type === "assessment")
      return !a.deadline;
    return false;
  };

  const updateAction = (idx, field, value) => {
    setActions(
      actions.map((a, i) => (i === idx ? { ...a, [field]: value } : a)),
    );
  };

  const removeAction = (idx) => {
    setActions(actions.filter((_, i) => i !== idx));
  };

  // datetime-local gives "2026-09-28T10:00" with no timezone. Converting
  // through Date reads it as the employer's local time and sends UTC, so
  // the candidate sees the same moment in their own timezone.
  const toUtcIso = (value) => (value ? new Date(value).toISOString() : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalStatus = status === "__custom__" ? customStatus : status;
    if (!finalStatus.trim()) return;
    if (actions.some(actionHasPastTime)) {
      setError("Meeting times and deadlines can't be in the past.");
      return;
    }
    if (actions.some(actionMissingTime)) {
      setError(
        "Meetings need a date and time, and requests and assessments need a deadline.",
      );
      return;
    }
    setSaving(true);
    setError(null);

    try {
      await api.post(
        // The application id comes from the URL and the employer JWT
        // authorises the push.
        `/employer/applications/${applicationId}/status`,
        {
          application_id: applicationId,
          status: finalStatus,
          note: note || null,
          actions: actions.map((a) => ({
            action_type: a.action_type,
            title: a.title,
            description: a.description || null,
            meeting_at: toUtcIso(a.meeting_at),
            duration_minutes: a.duration_minutes
              ? parseInt(a.duration_minutes)
              : null,
            attendees: a.attendees
              ? a.attendees
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [],
            url: a.url || null,
            deadline: toUtcIso(a.deadline),
            guidelines: a.guidelines || null,
          })),
        },
        authConfig,
      );
      onPushed();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Push status update
            </h2>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          id="push-form"
          className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5"
        >
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div>
            <label className={labelClass}>Status *</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={inputClass}
            >
              <option value="">Select status...</option>
              {DEFAULT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              <option value="__custom__">+ Custom status...</option>
            </select>
            {status === "__custom__" && (
              <input
                value={customStatus}
                onChange={(e) => setCustomStatus(e.target.value)}
                placeholder="Enter custom status"
                className={`${inputClass} mt-2`}
              />
            )}
          </div>

          <div>
            <label className={labelClass}>Note to candidate</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional message shown to the candidate..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          {/* Correspondence actions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className={`${labelClass} mb-0`}>
                Correspondence actions
              </label>
              <button
                type="button"
                onClick={addAction}
                className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
              >
                + Add action
              </button>
            </div>
            {actions.length === 0 && (
              <p className="text-xs text-gray-400 italic">
                No actions added. Click "+ Add action" to attach a meeting
                invite, document request, or assessment.
              </p>
            )}
            {actions.map((action, idx) => (
              <div
                key={idx}
                className="bg-gray-50 dark:bg-brand-dark-bg rounded-xl p-4 mb-3 border border-gray-100 dark:border-white/10"
              >
                <div className="flex items-center justify-between mb-3">
                  <select
                    value={action.action_type}
                    onChange={(e) =>
                      updateAction(idx, "action_type", e.target.value)
                    }
                    className={`${inputClass} w-auto`}
                  >
                    <option value="meeting">Meeting invite</option>
                    <option value="document_request">Document request</option>
                    <option value="assessment">Assessment</option>
                    <option value="info">Information</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeAction(idx)}
                    className="text-gray-300 hover:text-red-500 transition ml-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="flex flex-col gap-2">
                  <input
                    required
                    value={action.title}
                    onChange={(e) => updateAction(idx, "title", e.target.value)}
                    placeholder="Title *"
                    className={inputClass}
                  />
                  <textarea
                    value={action.description}
                    onChange={(e) =>
                      updateAction(idx, "description", e.target.value)
                    }
                    placeholder="Description"
                    rows={2}
                    className={`${inputClass} resize-none`}
                  />

                  {action.action_type === "meeting" && (
                    <>
                      <label className="block text-xs text-gray-500 dark:text-gray-400">
                        {action.action_type === "meeting" ? "Date and time *" : "Deadline *"}
                      </label>
                      <input
                        required
                        type="datetime-local"
                        value={action.meeting_at}
                        onChange={(e) =>
                          updateAction(idx, "meeting_at", e.target.value)
                        }
                        className={inputClass}
                      />
                      <input
                        value={action.duration_minutes}
                        onChange={(e) =>
                          updateAction(idx, "duration_minutes", e.target.value)
                        }
                        placeholder="Duration (minutes)"
                        type="number"
                        className={inputClass}
                      />
                      <input
                        value={action.attendees}
                        onChange={(e) =>
                          updateAction(idx, "attendees", e.target.value)
                        }
                        placeholder="Attendees (comma separated)"
                        className={inputClass}
                      />
                    </>
                  )}
                  {action.action_type === "assessment" && (
                    <input
                      value={action.url}
                      onChange={(e) => updateAction(idx, "url", e.target.value)}
                      placeholder="Assessment URL"
                      className={inputClass}
                    />
                  )}
                  {(action.action_type === "document_request" ||
                    action.action_type === "assessment") && (
                    <>
                      <label className="block text-xs text-gray-500 dark:text-gray-400">
                        {action.action_type === "meeting" ? "Date and time *" : "Deadline *"}
                      </label>
                      <input
                        required
                        type="datetime-local"
                        value={action.deadline}
                        onChange={(e) =>
                          updateAction(idx, "deadline", e.target.value)
                        }
                        placeholder="Deadline"
                        className={inputClass}
                      />
                      <input
                        value={action.guidelines}
                        onChange={(e) =>
                          updateAction(idx, "guidelines", e.target.value)
                        }
                        placeholder="Guidelines"
                        className={inputClass}
                      />
                    </>
                  )}
                  {action.action_type === "info" && (
                    <input
                      value={action.url}
                      onChange={(e) => updateAction(idx, "url", e.target.value)}
                      placeholder="Link URL (optional)"
                      className={inputClass}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-white/10 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="push-form"
            disabled={
              saving ||
              !status ||
              (status === "__custom__" && !customStatus.trim())
            }
            className="flex-1 bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
          >
            {saving ? "Pushing..." : "Push update"}
          </button>
        </div>
      </div>
    </>
  );
}
